import { useEffect, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

import { geoAddress, loadGeoCache, saveGeoCache } from "./geocache";
import type { ProbateLead } from "./types";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Probate Airtable rows rarely ship with lat/lng, so they'd never plot.
 * Geocode the missing ones client-side (throttled ~11/s, localStorage-cached)
 * and return an immutable coordinate overlay keyed by lead id — the query
 * data itself is never mutated.
 */
export const useProbateGeocoding = (
  probates: ProbateLead[] | undefined,
): Map<string, { lat: number; lng: number }> => {
  const geocodingLib = useMapsLibrary("geocoding");
  const [coords, setCoords] = useState<
    Map<string, { lat: number; lng: number }>
  >(() => new Map());
  const runningRef = useRef(false);

  useEffect(() => {
    if (!geocodingLib || !probates?.length || runningRef.current) return;
    const missing = probates.filter(
      (p) => (!p.lat || !p.lng) && geoAddress(p).length > 6,
    );
    if (!missing.length) return;

    runningRef.current = true;
    let cancelled = false;
    const cache = loadGeoCache();

    // 1) Fill from cache instantly.
    const fromCache = new Map<string, { lat: number; lng: number }>();
    const toGeocode: ProbateLead[] = [];
    for (const p of missing) {
      const hit = cache[geoAddress(p)];
      if (hit) fromCache.set(p.id, hit);
      else toGeocode.push(p);
    }
    if (fromCache.size) {
      setCoords((prev) => new Map([...prev, ...fromCache]));
    }

    // 2) Geocode the rest, throttled well under Google's rate limit.
    (async () => {
      const geocoder = new geocodingLib.Geocoder();
      let done = 0;
      let batch = new Map<string, { lat: number; lng: number }>();
      for (const p of toGeocode) {
        if (cancelled) break;
        const address = geoAddress(p);
        try {
          const r = await geocoder.geocode({ address });
          const loc = r.results?.[0]?.geometry?.location;
          if (loc) {
            const hit = { lat: loc.lat(), lng: loc.lng() };
            cache[address] = hit;
            batch.set(p.id, hit);
          }
        } catch (e: unknown) {
          if (String(e).includes("OVER_QUERY_LIMIT")) await sleep(1500);
        }
        // Incremental persist + render every 10 lookups.
        if (++done % 10 === 0) {
          saveGeoCache(cache);
          if (batch.size) {
            const flush = batch;
            batch = new Map();
            setCoords((prev) => new Map([...prev, ...flush]));
          }
        }
        await sleep(90);
      }
      saveGeoCache(cache);
      if (batch.size && !cancelled) {
        setCoords((prev) => new Map([...prev, ...batch]));
      }
      runningRef.current = false;
    })();

    return () => {
      cancelled = true;
      runningRef.current = false;
    };
  }, [geocodingLib, probates]);

  return coords;
};

/** Apply the geocoded overlay to the probate list (immutable). */
export const withGeocodedCoords = (
  probates: ProbateLead[],
  overlay: Map<string, { lat: number; lng: number }>,
): ProbateLead[] =>
  overlay.size
    ? probates.map((p) => {
        const hit = !p.lat || !p.lng ? overlay.get(p.id) : undefined;
        return hit ? { ...p, ...hit } : p;
      })
    : probates;
